import React, {useEffect, useRef} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useGetAudienceRecalculationStatus} from "../apis/requests/audiences/getAudienceRecalculationStatus";
import {parseUtc, StatusBanner} from "./StatusBanner";

const RUNNING_POLL_INTERVAL_MS = 30000;
const UP_TO_DATE_TITLE = "Audiences are up to date";
const RECALCULATING_TITLE = "Recalculating audiences";
const QUEUED_TEXT = "Another update is queued";
const NOT_RECALCULATED_TEXT = "Not recalculated yet";
const REFRESH_LABEL = "Refresh status";

type Props = {
  dataQueryKey: string;
};

const useRefreshDataWhenRecalculationFinishes = (isRunning: boolean, dataQueryKey: string) => {
  const queryClient = useQueryClient();
  const wasRunning = useRef(isRunning);

  useEffect(() => {
    if (wasRunning.current && !isRunning) {
      queryClient.invalidateQueries([dataQueryKey]);
    }
    wasRunning.current = isRunning;
  }, [isRunning, dataQueryKey, queryClient]);
};

export const AudienceRecalculationBanner = ({dataQueryKey}: Props) => {
  const queryClient = useQueryClient();
  const {
    data: status,
    refetch,
    isFetching,
  } = useGetAudienceRecalculationStatus({
    refetchInterval: (data) => (data?.is_running ? RUNNING_POLL_INTERVAL_MS : false),
  });
  const isRunning = status?.is_running ?? false;
  useRefreshDataWhenRecalculationFinishes(isRunning, dataQueryKey);

  if (!status) {
    return null;
  }

  const refresh = () => {
    refetch();
    queryClient.invalidateQueries([dataQueryKey]);
  };

  const lastCompleted = status.last_completed_at
    ? `Last recalculated ${parseUtc(status.last_completed_at).toLocaleString()}`
    : NOT_RECALCULATED_TEXT;
  const subtitle = status.rerun_pending ? `${lastCompleted} · ${QUEUED_TEXT}` : lastCompleted;
  const title = isRunning ? `${RECALCULATING_TITLE}… ${status.progress_percent}%` : UP_TO_DATE_TITLE;

  return (
    <StatusBanner
      title={title}
      subtitle={subtitle}
      isRunning={isRunning}
      progressPercent={status.progress_percent}
      refreshLabel={REFRESH_LABEL}
      onRefresh={refresh}
      isRefreshing={isFetching}
    />
  );
};
